import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../context/auth-provider';
import { can, PERMISSIONS } from '../../auth/permissions';
import { getLatestHandoff, markHandoffRead } from '../../api/handoff-client';
import { Handoff } from '../../interfaces/handoff-interfaces';

/**
 * THE ARRIVAL STATE FOR D-25 POINT 4 — "when the recipient logs in, their IQ
 * already has it; they do not have to ask."
 *
 * ═══════════════ WHY THIS IS APP-WIDE AND NOT INSIDE <IqChat> ═══════════════
 * IqChat only fetches once it is OPEN (useBuildingData is passed
 * `{ enabled: isOpen }`). A handover the reader has to open the chat to discover
 * is a handover they have to ask for, which is the exact behaviour D-25 exists
 * to replace. So the one cheap read that answers "is anything waiting for me"
 * lives here, above the chat, and drives two surfaces that are visible without
 * opening anything: the badge on the IQ entry point, and the line on the shift
 * clock at the top of the main menu.
 *
 * ═══════════════ IT IS DELIBERATELY NOT GATED ON THE IQ ENTITLEMENT ═════════
 * `IqChat` self-suppresses for an account without Lobbero IQ, and correctly so —
 * the aurora mark is reserved. But POST /shifts/handoff needs only `shift:clock`
 * and the log-read pair; when the model is unavailable it degrades to the
 * deterministic floor and the handover still happens. An unentitled account can
 * therefore hold real handovers, and gating this provider on the entitlement
 * would hide them from the only people they were addressed to. The shift clock
 * is the surface that reaches those readers, which is why it renders the record
 * itself rather than only linking into the chat.
 *
 * ═══════════════ NO POLLING LOOP ═══════════════
 * Mount + window focus. A desk tablet sits on one screen for eight hours, and an
 * interval against every logged-in staff member buys a fresher badge with a
 * permanent request rate. Focus covers the case that actually happens: somebody
 * comes back to the tab after their colleague handed over.
 */

interface HandoffContextValue {
  /** The newest handover the LAST READ returned, whatever desk it was about.
   *
   *  USE `latestFor(buildingId)` ON ANY SURFACE THAT SHOWS ONE DESK. This value
   *  is deliberately un-scoped: it backs the floating launcher, which has no
   *  building context and legitimately means "anywhere you can see". Rendering
   *  it beside a named building is what put one desk's handover under another
   *  desk's heading. */
  latest: Handoff | null;
  /** THE SAFE READ. Returns the handover only when the answer in hand was
   *  ACTUALLY ABOUT `buildingId` — the server stamps every reply with
   *  `for_building`, and this compares the two.
   *
   *  That makes the wrong render impossible rather than unlikely: during the
   *  first paint, before `setBuilding` has landed and the re-read has returned,
   *  the answer in hand is the previous desk's and this returns null instead of
   *  it. A blank card for one frame is a correct blank card; the alternative was
   *  a confident wrong one.
   *
   *  Passing null asks the un-scoped question and gets the un-scoped answer. */
  latestFor: (buildingId: number | null) => Handoff | null;
  unreadCount: number;
  /** `unreadCount` narrowed to the building last asked about, or null when the
   *  answer in hand is not about that desk. Same contract as `latestFor`. */
  unreadHereFor: (buildingId: number | null) => number | null;
  loading: boolean;
  /** TRUE when this reader can hold handovers at all (`shift:clock`). False for
   *  a resident or a role without the clock, and every surface stays silent. */
  supported: boolean;
  refresh: () => Promise<void>;
  /**
   * WHICH DESK THE ANSWER IS ABOUT.
   *
   * A handover belongs to a building, and everyone who covers more than one —
   * every property manager, and a floating concierge — was being handed "the
   * newest handover anywhere in my scope", which is usually not the desk they
   * are looking at. The shell tells the provider which building is selected and
   * the read is re-run for it.
   *
   * null is legitimate and means "anywhere in scope": a page with no building
   * context (the badge on a dashboard tab) still wants an answer.
   */
  setBuilding: (buildingId: number | null) => void;
  /**
   * THE OVERLAY'S DECLARATION, which OUTRANKS the page's while it exists.
   *
   * There are two kinds of surface that know which desk the reader means, and
   * they are not peers. A PAGE (the shift clock on the main menu) is showing one
   * desk for as long as the reader is on it. An OVERLAY (the IQ chat, which is
   * mounted app-wide and floats above whatever page is beneath it) is showing a
   * desk only while it is open.
   *
   * Treating them as one mutable value produced two failures in turn: with the
   * chat declaring unconditionally it published "no building" from every screen
   * in the product, because a closed chat has resolved no building — and it
   * blanked the handover the gate had just been handed. Making the page
   * re-assert instead would have had the two writing over each other on any
   * screen that has both.
   *
   * So the overlay declares while it is open and RELEASES on close (pass
   * `undefined`), and the page's declaration is what is underneath the whole
   * time. Nothing has to win a race, because they are not in one.
   */
  setOverlayBuilding: (buildingId: number | null | undefined) => void;
  /** Mark read AND fold the result back into this state, so the badge drops the
   *  moment the card is opened rather than on the next focus.
   *
   *  RESOLVES TO THE SERVER'S ROW, or null when the write did not happen. The
   *  caller gets the authoritative `read_at` rather than having to guess one:
   *  an optimistic local timestamp would show "opened" on a row the server never
   *  accepted, and this is the field a supervisor reads. */
  markRead: (id: string) => Promise<Handoff | null>;
}

const HandoffContext = createContext<HandoffContextValue | null>(null);

export const HandoffProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, permissions, onboarding } = useAuth();
  const supported =
    !!user && !!onboarding?.completed && can(permissions, PERMISSIONS.SHIFT_CLOCK);

  /* THE ANSWER AND THE QUESTION IT ANSWERED, IN ONE PIECE OF STATE.
     Held together rather than in two useStates so they cannot be observed apart:
     a render that saw the new building and the old row is exactly the bug this
     is here to prevent, and two setState calls in one handler still produce that
     pair if anything between them suspends. `forBuilding` is the server's own
     `for_building`, never the id we asked with — if the two ever disagree, the
     server's is the truth about the row it sent. */
  const [answer, setAnswer] = useState<{
    forBuilding: number | null;
    row: Handoff | null;
    unreadHere: number | null;
  }>({ forBuilding: null, row: null, unreadHere: null });
  const [pageBuildingId, setPageBuildingId] = useState<number | null>(null);
  /* `undefined` means the overlay is not declaring anything and the page's
     value stands. `null` is a real declaration meaning "anywhere in scope". */
  const [overlayBuildingId, setOverlayBuildingId] =
    useState<number | null | undefined>(undefined);
  const buildingId =
    overlayBuildingId === undefined ? pageBuildingId : overlayBuildingId;
  const [unreadCount, setUnreadCount] = useState(0);
  const latest = answer.row;
  const [loading, setLoading] = useState(false);
  // Guards the unmount-after-await write and, more usefully, stops a focus
  // refresh that landed after logout from repopulating a signed-out badge.
  const aliveRef = useRef(true);
  /* SUPERSEDE, NOT SKIP.
     This was "one in flight at a time; drop the rest", which is right for the
     case it was written for — a bored desk alt-tabbing, where every request asks
     the SAME question. It is wrong the moment the question changes: selecting
     another building while the previous read is in flight dropped the read for
     the building now on screen, and the answer in hand stayed the old desk's
     until something else happened to trigger a refresh.
     Now every call fires and each result is discarded unless it is the newest —
     the last question asked is the one whose answer lands. */
  const seqRef = useRef(0);

  useEffect(() => {
    aliveRef.current = true;

    return () => {
      aliveRef.current = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (!supported) return;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    setLoading(true);
    try {
      const res = await getLatestHandoff(buildingId);
      // A slower answer to an older question must never overwrite a newer one.
      if (!aliveRef.current || seq !== seqRef.current) return;
      /* STAMPED WITH THE SERVER'S OWN SCOPE, not with the id we asked using.
         `for_building` is what the row is actually about; trusting our own
         request would re-introduce the race the stamp exists to close. An older
         server that does not send it is treated as un-scoped, which is what it
         was. */
      setAnswer({
        forBuilding:
          typeof res.for_building === 'number' ? res.for_building : null,
        row: res.latest,
        unreadHere:
          typeof res.unread_here === 'number' ? res.unread_here : null,
      });
      setUnreadCount(res.unread_count || 0);
    } catch {
      // SILENT, AND THAT IS A DECISION. This runs unprompted on every page load
      // for every staff member; a toast here would put "Could not read your
      // latest handover" in front of somebody who never asked for one, on every
      // flaky connection. The surfaces that render this state show nothing when
      // it is empty, so a failed read degrades to "no handover waiting" — the
      // same thing the reader sees on a perfectly healthy quiet day. The
      // handover itself is never lost: nothing deletes it, and the next refresh
      // finds it.
      if (aliveRef.current && seq === seqRef.current) {
        setAnswer({ forBuilding: null, row: null, unreadHere: null });
      }
    } finally {
      // Only the newest request owns the spinner; an older one finishing must
      // not declare the screen settled while the current read is still running.
      if (aliveRef.current && seq === seqRef.current) setLoading(false);
    }
    // buildingId is a dependency, so selecting another building re-reads rather
    // than leaving the previous desk's handover on screen under a new name.
  }, [supported, buildingId]);

  useEffect(() => {
    if (!supported) {
      setAnswer({ forBuilding: null, row: null, unreadHere: null });
      setUnreadCount(0);

      return;
    }
    refresh();
  }, [supported, refresh]);

  useEffect(() => {
    if (!supported) return undefined;
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [supported, refresh]);

  const setBuilding = useCallback((next: number | null): void => {
    // Guarded so a parent that re-renders on every keystroke cannot restart the
    // read. Number.isInteger keeps a NaN (an unselected building parsed to a
    // number) out of the query string.
    setPageBuildingId(current => {
      const value = next != null && Number.isInteger(next) ? next : null;

      return value === current ? current : value;
    });
  }, []);

  const setOverlayBuilding = useCallback(
    (next: number | null | undefined): void => {
      setOverlayBuildingId(current => {
        if (next === undefined) return current === undefined ? current : undefined;
        const value = next != null && Number.isInteger(next) ? next : null;

        return value === current ? current : value;
      });
    },
    []
  );

  /* ── THE TWO SAFE READS ──
     A surface showing ONE desk asks for that desk by id and gets an answer only
     if the answer in hand is about that desk. Everything else — a stale read
     still in flight, a provider that has not been told the building yet, two
     writers (ShiftClock and IqChat) racing to declare different buildings — ends
     up as null here rather than as another desk's handover rendered under this
     desk's name.

     A caller passing null is asking the un-scoped question on purpose (the
     floating launcher) and gets the un-scoped answer. */
  const latestFor = useCallback(
    (want: number | null): Handoff | null => {
      const wanted = want != null && Number.isInteger(want) ? want : null;

      return answer.forBuilding === wanted ? answer.row : null;
    },
    [answer]
  );

  const unreadHereFor = useCallback(
    (want: number | null): number | null => {
      const wanted = want != null && Number.isInteger(want) ? want : null;
      if (answer.forBuilding !== wanted) return null;

      return wanted === null ? unreadCount : answer.unreadHere;
    },
    [answer, unreadCount]
  );

  const markRead = useCallback(async (id: string): Promise<Handoff | null> => {
    try {
      const res = await markHandoffRead(id);
      if (!aliveRef.current) return res.handoff;
      setAnswer(prev =>
        prev.row && prev.row.id === id ? { ...prev, row: res.handoff } : prev
      );
      // `already_read` means the timestamp was set on an earlier open, so the
      // count was already decremented then. Decrementing again would drive it
      // negative on a re-open.
      if (!res.already_read) setUnreadCount(n => Math.max(0, n - 1));

      return res.handoff;
    } catch {
      // A 403 here is CORRECT BEHAVIOUR, not a failure: only the recipient may
      // mark a handover read, so a manager reading the desk's history lands
      // exactly here. Swallowing it keeps their screen honest — the record they
      // are looking at genuinely is still unread, and telling them otherwise
      // would destroy the one signal the column carries.
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({
      latest,
      latestFor,
      unreadCount,
      unreadHereFor,
      loading,
      supported,
      refresh,
      setBuilding,
      setOverlayBuilding,
      markRead,
    }),
    [latest, latestFor, unreadCount, unreadHereFor, loading, supported, refresh,
      setBuilding, setOverlayBuilding, markRead]
  );

  return (
    <HandoffContext.Provider value={value}>{children}</HandoffContext.Provider>
  );
};

/** Returns an INERT value outside the provider rather than throwing.
 *
 *  The opposite call from useIqChat, and for a different reason: a chat tile
 *  outside its provider is a wiring bug that must fail loudly, but this hook is
 *  consumed by the shift clock, which renders for accounts that never bought
 *  Lobbero IQ and on screens mounted outside the app shell. A throw there would
 *  take down the clock-in button — the one control the backend guarantees works
 *  with ai_config.enabled = false — over a badge. */
const INERT: HandoffContextValue = {
  latest: null,
  latestFor: () => null,
  unreadHereFor: () => null,
  unreadCount: 0,
  loading: false,
  supported: false,
  refresh: async () => undefined,
  setBuilding: () => undefined,
  setOverlayBuilding: () => undefined,
  markRead: async () => null,
};

export const useHandoffInbox = (): HandoffContextValue =>
  useContext(HandoffContext) || INERT;
